import React from 'react';

function renderInline(text, keyPrefix = 'inline') {
  const tokenPattern = /(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*)/g;
  const parts = text.split(tokenPattern).filter(Boolean);

  return parts.map((part, index) => {
    const key = `${keyPrefix}-${index}`;
    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      const external = /^https?:\/\//.test(link[2]);
      return <a key={key} href={link[2]} target={external ? '_blank' : undefined} rel={external ? 'noreferrer' : undefined}>{link[1]}</a>;
    }
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={key}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('`') && part.endsWith('`')) return <code key={key}>{part.slice(1, -1)}</code>;
    if (part.startsWith('*') && part.endsWith('*')) return <em key={key}>{part.slice(1, -1)}</em>;
    return <React.Fragment key={key}>{part}</React.Fragment>;
  });
}

function parseMarkdown(markdown) {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let index = 0;

  const isSpecial = (line) => /^(#{2,3})\s+/.test(line) || /^>\s?/.test(line) || /^-\s+/.test(line) || /^---$/.test(line) || /^```/.test(line);

  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim()) { index += 1; continue; }

    const heading = line.match(/^(#{2,3})\s+(.+)$/);
    if (heading) {
      blocks.push({ type: heading[1].length === 2 ? 'h2' : 'h3', text: heading[2] });
      index += 1;
      continue;
    }

    if (/^---$/.test(line)) {
      blocks.push({ type: 'hr' });
      index += 1;
      continue;
    }

    if (/^```/.test(line)) {
      const code = [];
      index += 1;
      while (index < lines.length && !/^```/.test(lines[index])) {
        code.push(lines[index]);
        index += 1;
      }
      index += 1;
      blocks.push({ type: 'code', text: code.join('\n') });
      continue;
    }

    if (/^>\s?/.test(line)) {
      const quote = [];
      while (index < lines.length && /^>\s?/.test(lines[index])) {
        quote.push(lines[index].replace(/^>\s?/, ''));
        index += 1;
      }
      blocks.push({ type: 'quote', text: quote.join(' ') });
      continue;
    }

    if (/^-\s+/.test(line)) {
      const items = [];
      while (index < lines.length && /^-\s+/.test(lines[index])) {
        items.push(lines[index].replace(/^-\s+/, ''));
        index += 1;
      }
      blocks.push({ type: 'list', items });
      continue;
    }

    const paragraph = [line.trim()];
    index += 1;
    while (index < lines.length && lines[index].trim() && !isSpecial(lines[index])) {
      paragraph.push(lines[index].trim());
      index += 1;
    }
    blocks.push({ type: 'paragraph', text: paragraph.join(' ') });
  }
  return blocks;
}

const EDITORIAL_QUOTES = {
  '01': [
    {
      parts: ['Per questo il montaggio televisivo è un lavoro editoriale prima ancora che tecnico.'],
      label: 'Responsabilità editoriale',
      timecode: '01:16:08:12',
    },
    {
      parts: ['Un progetto comprensibile soltanto alla persona che lo ha creato non è un progetto professionale: è un rischio per l’intera produzione.'],
      label: 'Progetti condivisi',
      timecode: '01:38:24:03',
    },
  ],
  '02': [
    {
      parts: ['Un bravo ingest non si riconosce soltanto dalla velocità con cui esegue le operazioni richieste, ma dalla quantità di problemi che riesce a prevenire prima che qualcuno si accorga della loro esistenza.'],
      label: 'Prevenzione / Workflow',
      timecode: '02:22:17:11',
    },
    {
      parts: ['Il montatore costruisce l’episodio.', 'L’ingest costruisce le condizioni perché quell’episodio possa essere montato, modificato, finalizzato e consegnato.'],
      label: 'Ingest / Montaggio',
      timecode: '02:51:06:18',
    },
  ],
  '03': [
    {
      parts: ['Il problema non è che esista la raccomandazione.', 'Il problema è che, troppo spesso, non esiste nulla prima della raccomandazione.'],
      label: 'Fiducia / Accesso',
      timecode: '03:14:35:09',
    },
    {
      parts: ['Il talento può crescere ovunque.', 'Le occasioni, invece, tendono a concentrarsi negli stessi luoghi.'],
      label: 'Talento / Occasioni',
      timecode: '03:37:18:22',
    },
  ],
  '04': [
    {
      parts: ['La post-produzione non si limita quindi a ridurre la durata del materiale, ma deve trovare al suo interno una forma narrativa.'],
      label: 'Costruzione narrativa',
      timecode: '04:18:42:07',
    },
    {
      parts: ['Quando una decisione viene rimandata, il problema non scompare. Si sposta semplicemente verso la fine della filiera, dove il margine di intervento è minore.'],
      label: 'Tempi / Filiera',
      timecode: '04:46:11:16',
    },
  ],
  '05': [
    {
      parts: ['Il software giusto non è necessariamente quello storicamente più utilizzato né quello tecnicamente più innovativo. È quello che rende sostenibile l’intera produzione.'],
      label: 'Scelta del software',
      timecode: '05:27:09:14',
    },
    {
      parts: ['Il software può cambiare. Le competenze che permettono di comprendere una produzione restano.'],
      label: 'Competenze / Strumenti',
      timecode: '05:53:20:04',
    },
  ],
  '06': [
    {
      parts: ['Il problema nasce quando l’elenco dei programmi viene considerato una descrizione completa del professionista.'],
      label: 'Crediti / Contesto',
      timecode: '06:12:32:19',
    },
    {
      parts: ['Aver lavorato a un programma racconta una parte della storia.', 'Capire come ci hai lavorato racconta il professionista.'],
      label: 'Esperienza / Professionista',
      timecode: '06:48:03:12',
    },
  ],
  '07': [
    {
      parts: ['La post-produzione può risolvere problemi difficili.', 'Questo non significa che ogni soluzione sia conveniente.'],
      label: 'Possibile / Conveniente',
      timecode: '07:23:44:08',
    },
    {
      parts: ['Il problema non è il compromesso.', 'È fingere che non esista.'],
      label: 'Compromesso / Consapevolezza',
      timecode: '07:45:16:21',
    },
  ],
  '08': [
    {
      parts: ['Il montatore non teme le modifiche.', 'Teme la distanza tra l’aggettivo “piccola” e la realtà che sta per scoprire.'],
      label: 'Piccola modifica / Realtà',
      timecode: '08:15:28:05',
    },
    {
      parts: ['A quel punto non si sta aumentando la visibilità di una persona.', 'Si sta cercando di dimostrare che fosse più presente di quanto il girato permetta realmente di raccontare.'],
      label: 'Presenza / Girato',
      timecode: '08:39:51:17',
    },
  ],
  '09': [
    {
      parts: ['Il montatore costruisce il proprio pezzo.', 'Il finalizzatore deve ricordarsi che lo spettatore vedrà la puntata intera.'],
      label: 'Scena / Puntata',
      timecode: '09:17:08:23',
    },
    {
      parts: ['Il finalizzatore eredita spesso tutti i problemi.', 'I meriti, invece, rimangono distribuiti molto democraticamente.'],
      label: 'Responsabilità / Meriti',
      timecode: '09:47:36:10',
    },
  ],
  '10': [
    {
      parts: ['Un buon pezzo contiene una storia.', 'Un buon montaggio trova il modo migliore per raccontarla.'],
      label: 'Taglio contenuti / Montaggio',
      timecode: '10:18:42:07',
    },
    {
      parts: ['Il fatto che il reparto riesca comunque a consegnare non dimostra che il carico sia sostenibile.', 'Dimostra che qualcuno sta compensando la struttura con il proprio tempo personale.'],
      label: 'Carico reale / Ore aggiuntive',
      timecode: '10:42:16:19',
    },
  ],
};

function addEditorialBreaks(blocks, articleNumber, editorialMode) {
  if (!editorialMode) return blocks;

  const quotes = EDITORIAL_QUOTES[articleNumber] || [];
  const result = [];

  for (let index = 0; index < blocks.length; index += 1) {
    const match = quotes.find((item) => item.parts.every((part, offset) => {
      const candidate = blocks[index + offset];
      return candidate?.type === 'paragraph' && candidate.text === part;
    }));

    if (match) {
      result.push({ type: 'pullquote', text: match.parts.join(' '), label: match.label, timecode: match.timecode });
      index += match.parts.length - 1;
    } else {
      result.push(blocks[index]);
    }
  }
  return result;
}

function renderBlock(block, key, options = {}) {
  const { firstParagraph = false, sectionNumber = null } = options;

  if (block.type === 'h2') {
    if (sectionNumber !== null) {
      return (
        <div className="inside-section-heading" key={key}>
          <span>{String(sectionNumber).padStart(2, '0')}</span>
          <h2>{renderInline(block.text, key)}</h2>
        </div>
      );
    }
    return <h2 key={key}>{renderInline(block.text, key)}</h2>;
  }
  if (block.type === 'h3') return <h3 key={key}>{renderInline(block.text, key)}</h3>;
  if (block.type === 'quote') return <blockquote key={key}>{renderInline(block.text, key)}</blockquote>;
  if (block.type === 'list') return <ul key={key}>{block.items.map((item, itemIndex) => <li key={`${key}-${itemIndex}`}>{renderInline(item, `${key}-${itemIndex}`)}</li>)}</ul>;
  if (block.type === 'code') return <pre key={key}><code>{block.text}</code></pre>;
  if (block.type === 'hr') return <hr key={key} />;
  if (block.type === 'pullquote') {
    return (
      <aside className="inside-pullquote" key={key}>
        <div className="inside-pullquote-meta"><span>{block.label}</span><span>{block.timecode}</span></div>
        <p>{renderInline(block.text, key)}</p>
        <div className="inside-pullquote-track" aria-hidden="true"><i /><i /><i /></div>
      </aside>
    );
  }
  return <p key={key} className={firstParagraph ? 'inside-prose-lead' : undefined}>{renderInline(block.text, key)}</p>;
}

export default function MarkdownRenderer({ markdown, editorialMode = false, articleNumber = '' }) {
  const blocks = addEditorialBreaks(parseMarkdown(markdown), articleNumber, editorialMode);
  const rendered = [];
  let index = 0;
  let paragraphSeen = false;
  let sectionNumber = 0;

  while (index < blocks.length) {
    const block = blocks[index];
    const key = `block-${index}`;
    const isRealLife = (block.type === 'h2' || block.type === 'h3') && block.text.trim().toLowerCase() === 'nel lavoro vero';

    if (editorialMode && isRealLife) {
      const sectionBlocks = [];
      index += 1;
      while (index < blocks.length && blocks[index].type !== 'h2') {
        sectionBlocks.push(blocks[index]);
        index += 1;
      }

      rendered.push(
        <section className="inside-work-real" key={key}>
          <div className="inside-work-real-head"><span>JKL_INSIDE / REAL_LIFE</span><span>REC ●</span></div>
          <div className="inside-work-real-body">
            <p className="inside-work-real-kicker">Fuori dal piano di lavorazione</p>
            <h2>Nel lavoro vero</h2>
            {sectionBlocks.map((sectionBlock, childIndex) => renderBlock(sectionBlock, `${key}-${childIndex}`))}
          </div>
          <div className="inside-work-real-timeline" aria-hidden="true"><span /><span /><span /><i /></div>
        </section>
      );
      continue;
    }

    if (block.type === 'h2' && editorialMode) {
      sectionNumber += 1;
      rendered.push(renderBlock(block, key, { sectionNumber }));
    } else {
      const firstParagraph = editorialMode && block.type === 'paragraph' && !paragraphSeen;
      rendered.push(renderBlock(block, key, { firstParagraph }));
      if (block.type === 'paragraph') paragraphSeen = true;
    }
    index += 1;
  }

  return <div className={`inside-prose${editorialMode ? ' inside-prose--editorial' : ''}`}>{rendered}</div>;
}
